import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { appliedRateSchema, currencyCodeSchema, journalEntryViewSchema, moneySchema, signedAmountSchema, } from '../finance/finance.js';
import { Percentage } from '../finance/percentage.js';
import { bankAccountRefSchema, citedDecisionSchema, documentStatusSchema, founderRefSchema, } from '../funding/funding.js';
import { policyVersionStatusSchema } from '../governance/policies.js';
import { attachmentSchema, createLinkAttachmentRequestSchema } from '../governance/topics.js';
/**
 * Company reserve policy, restricted cash and distributions to founders (spec §7.16, §18.1, §19.4,
 * ADR-0009). Distributable cash is computed by deterministic backend code from the journal, the
 * approved reserve policy and recorded restrictions; nothing is ever paid automatically.
 */
// --- enums ----------------------------------------------------------------------------------------
export const RESERVE_POLICY_TYPES = [
    'FIXED_AMOUNT',
    'MONTHS_OF_EXPENSES',
    'PERCENTAGE_OF_CASH',
    'HYBRID',
];
export const reservePolicyTypeSchema = z.enum(RESERVE_POLICY_TYPES);
export const RESERVE_HYBRID_RULES = ['GREATER_OF', 'SUM_OF'];
export const reserveHybridRuleSchema = z.enum(RESERVE_HYBRID_RULES);
export const RESERVE_COMPONENT_KINDS = [
    'FIXED_AMOUNT',
    'MONTHS_OF_EXPENSES',
    'PERCENTAGE_OF_CASH',
];
export const reserveComponentKindSchema = z.enum(RESERVE_COMPONENT_KINDS);
export const CASH_RESTRICTION_PURPOSES = [
    'TAX',
    'PAYROLL',
    'CONTRACTUAL_LIABILITY',
    'APPROVED_BUDGET',
    'OTHER',
];
export const cashRestrictionPurposeSchema = z.enum(CASH_RESTRICTION_PURPOSES);
export const CASH_RESTRICTION_STATUSES = ['ACTIVE', 'RELEASED'];
export const cashRestrictionStatusSchema = z.enum(CASH_RESTRICTION_STATUSES);
export const DISTRIBUTION_STATUSES = [
    'PROPOSED',
    'APPROVED',
    'SCHEDULED',
    'PARTIALLY_PAID',
    'PAID',
    'CANCELLED',
    'REVERSED',
];
export const distributionStatusSchema = z.enum(DISTRIBUTION_STATUSES);
export const DISTRIBUTION_PAYMENT_STATUSES = [
    'PENDING',
    'SCHEDULED',
    'PARTIALLY_PAID',
    'PAID',
    'CANCELLED',
];
export const distributionPaymentStatusSchema = z.enum(DISTRIBUTION_PAYMENT_STATUSES);
export const DISTRIBUTION_WARNING_CODES = [
    'NO_RESERVE_POLICY',
    'NO_CAP_TABLE',
    'MISSING_FX_RATE',
    'NO_EXPENSE_DATA',
    'NO_OPERATING_ACCOUNTS',
    'NON_OPERATING_CASH_EXCLUDED',
    'NOTHING_DISTRIBUTABLE',
    'POOL_EXCEEDS_DISTRIBUTABLE',
    'POOL_TOO_SMALL',
    'COMPANY_OWES_FOUNDERS',
    'FOUNDER_DEBTS_NOT_NETTED',
    'DRAFT_TRANSACTIONS_EXCLUDED',
    'SCENARIO_ASSUMPTIONS',
];
export const distributionWarningCodeSchema = z.enum(DISTRIBUTION_WARNING_CODES);
/** BLOCKING: a proposal with these figures cannot be created or approved. */
export const WARNING_SEVERITIES = ['BLOCKING', 'WARNING', 'INFO'];
export const warningSeveritySchema = z.enum(WARNING_SEVERITIES);
// --- amounts --------------------------------------------------------------------------------------
/** Distributions are split and paid in whole cents, so amounts use at most 2 decimals. */
export const distributionAmountSchema = z
    .string()
    .trim()
    .regex(/^\d{1,16}(\.\d{1,4})?$/, 'Enter an amount such as 1250.50, with at most 2 decimals')
    .refine((value) => /[1-9]/.test(value), { message: 'The amount must be greater than zero' })
    .refine((value) => !/\.\d{3,}$/.test(value.replace(/0+$/, '')), {
    message: 'Use at most 2 decimals',
});
/** Months of expenses to hold, e.g. `3` or `4.5` (at most 2 decimals). */
export const reserveMonthsSchema = z
    .string()
    .trim()
    .regex(/^\d{1,4}(\.\d{1,2})?$/, 'Enter a number of months such as 3 or 4.5')
    .refine((value) => /[1-9]/.test(value), { message: 'Enter more than zero months' });
/** Percent of operating cash, e.g. `20` or `12.5` (0 < p ≤ 100, at most 4 decimals). */
export const reservePercentSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Enter a percentage such as 20 or 12.5')
    .refine((value) => /[1-9]/.test(value), { message: 'Enter a percentage above zero' })
    .refine((value) => Percentage.of(value).compare(Percentage.ONE_HUNDRED) <= 0, {
    message: 'The percentage cannot exceed 100',
});
// --- reserve policy -------------------------------------------------------------------------------
export const reservePolicySchema = z.object({
    id: uuidSchema,
    versionNumber: z.number().int(),
    status: policyVersionStatusSchema,
    effectiveFrom: isoDateSchema.nullable(),
    policyType: reservePolicyTypeSchema,
    minimumAmount: moneySchema.nullable(),
    runwayMonths: z.string().nullable(),
    expenseWindowMonths: z.number().int().nullable(),
    percentageOfCash: z.string().nullable(),
    hybridRule: reserveHybridRuleSchema.nullable(),
    /** The formula in plain words, e.g. "The greater of 10,000.00 USD and 3 months of expenses". */
    summary: z.string(),
    notes: z.string().nullable(),
    proposedBy: z.string().nullable(),
    approvalDecision: citedDecisionSchema.nullable(),
    approvalReference: z.string().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    rejectedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    /** The approved version in force today. */
    inForce: z.boolean(),
});
export const reservePolicyListSchema = z.object({
    items: z.array(reservePolicySchema),
    permissions: z.object({ canPropose: z.boolean(), canApprove: z.boolean() }),
});
const reservePolicyFields = {
    policyType: reservePolicyTypeSchema,
    /** Fixed minimum (FIXED_AMOUNT, optional HYBRID component), in `currency`. */
    minimumAmount: distributionAmountSchema.nullable().default(null),
    currency: currencyCodeSchema.nullable().default(null),
    /** Months of average monthly expenses to hold (MONTHS_OF_EXPENSES, optional HYBRID component). */
    runwayMonths: reserveMonthsSchema.nullable().default(null),
    /** Complete months averaged for expenses (1–36). */
    expenseWindowMonths: z.number().int().min(1).max(36).nullable().default(null),
    percentageOfCash: reservePercentSchema.nullable().default(null),
    hybridRule: reserveHybridRuleSchema.nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
};
export const proposeReservePolicyRequestSchema = z
    .object(reservePolicyFields)
    .superRefine((value, ctx) => {
    const fixed = value.minimumAmount !== null;
    const months = value.runwayMonths !== null;
    const percent = value.percentageOfCash !== null;
    const issue = (path, message) => {
        ctx.addIssue({ code: 'custom', message, path: [path] });
    };
    if (fixed !== (value.currency !== null)) {
        issue(fixed ? 'currency' : 'minimumAmount', 'A fixed amount needs its currency');
    }
    if (months !== (value.expenseWindowMonths !== null)) {
        issue(months ? 'expenseWindowMonths' : 'runwayMonths', 'Months of expenses need both the number of months and the averaging window');
    }
    switch (value.policyType) {
        case 'FIXED_AMOUNT':
            if (!fixed)
                issue('minimumAmount', 'Enter the minimum amount to keep');
            if (months || percent)
                issue('policyType', 'A fixed amount uses only the minimum amount');
            break;
        case 'MONTHS_OF_EXPENSES':
            if (!months)
                issue('runwayMonths', 'Enter how many months of expenses to keep');
            if (fixed || percent)
                issue('policyType', 'This policy uses only months of expenses');
            break;
        case 'PERCENTAGE_OF_CASH':
            if (!percent)
                issue('percentageOfCash', 'Enter the percentage of cash to keep');
            if (fixed || months)
                issue('policyType', 'This policy uses only a percentage of cash');
            break;
        case 'HYBRID':
            if ([fixed, months, percent].filter(Boolean).length < 2) {
                issue('policyType', 'A combined policy needs at least two of the three components');
            }
            if (value.hybridRule === null) {
                issue('hybridRule', 'Choose how the components combine');
            }
            break;
    }
    if (value.policyType !== 'HYBRID' && value.hybridRule !== null) {
        issue('hybridRule', 'Only a combined policy has a combination rule');
    }
});
export const approveReservePolicyRequestSchema = z
    .object({
    effectiveFrom: isoDateSchema,
    approvalDecisionId: uuidSchema.nullable().default(null),
    approvalReference: z.string().trim().max(500).nullable().default(null),
})
    .refine((value) => value.approvalDecisionId !== null || (value.approvalReference ?? '').trim().length >= 3, {
    message: 'Cite the finalized decision or the approval reference (e.g. signed minutes)',
    path: ['approvalReference'],
});
export const closeReservePolicyRequestSchema = z.object({ reason: reasonSchema });
// --- cash restrictions ----------------------------------------------------------------------------
export const cashRestrictionSchema = z.object({
    id: uuidSchema,
    purpose: cashRestrictionPurposeSchema,
    amount: moneySchema,
    effectiveFrom: isoDateSchema,
    effectiveTo: isoDateSchema.nullable(),
    reason: z.string(),
    decision: citedDecisionSchema.nullable(),
    status: cashRestrictionStatusSchema,
    /** Counts against distributable cash today. */
    activeToday: z.boolean(),
    releasedAt: isoDateTimeSchema.nullable(),
    releaseReason: z.string().nullable(),
    releasedBy: z.string().nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
    version: z.number().int(),
});
export const cashRestrictionListSchema = z.object({
    items: z.array(cashRestrictionSchema),
    permissions: z.object({ canManage: z.boolean() }),
});
export const createCashRestrictionRequestSchema = z
    .object({
    purpose: cashRestrictionPurposeSchema,
    amount: distributionAmountSchema,
    currency: currencyCodeSchema,
    effectiveFrom: isoDateSchema,
    effectiveTo: isoDateSchema.nullable().default(null),
    reason: reasonSchema,
    decisionId: uuidSchema.nullable().default(null),
})
    .refine((value) => value.effectiveTo === null || value.effectiveTo >= value.effectiveFrom, {
    message: 'The end date cannot be before the start date',
    path: ['effectiveTo'],
});
export const releaseCashRestrictionRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
// --- calculation ----------------------------------------------------------------------------------
export const distributionWarningSchema = z.object({
    code: distributionWarningCodeSchema,
    severity: warningSeveritySchema,
    message: z.string(),
});
export const reserveComponentSchema = z.object({
    kind: reserveComponentKindSchema,
    /** In the calculation currency; null when it cannot be computed (e.g. a missing FX rate). */
    amount: z.string().nullable(),
    explanation: z.string(),
    conversion: appliedRateSchema.nullable(),
});
export const founderObligationsSchema = z.object({
    founder: founderRefSchema,
    /** Open debts of the founder to the company (display only — never netted, open decision 9). */
    owesCompany: z.string(),
    owesFounders: z.string(),
    /** What the company owes the founder (advances, loans, reimbursements, credits). */
    owedByCompany: z.string(),
    owedByFounders: z.string(),
});
export const distributionAllocationLineSchema = z.object({
    founderProfileId: uuidSchema,
    founderName: z.string(),
    percentage: z.string(),
    /** Exact share before rounding (10 decimals). */
    exactAmount: z.string(),
    amount: z.string(),
    roundingAdjustment: z.string(),
});
export const distributionCalculationSchema = z.object({
    currency: z.string(),
    /** Cash, restrictions and the reserve policy are taken as of this date (workspace calendar). */
    calculatedOn: isoDateSchema,
    /** Ownership is taken from the cap table in force on this date. */
    recordDate: isoDateSchema,
    period: z.object({ from: isoDateSchema, to: isoDateSchema }),
    cash: z.object({
        accounts: z.array(z.object({
            id: uuidSchema,
            name: z.string(),
            isOperating: z.boolean(),
            balance: z.string(),
        })),
        /** Book balance of operating accounts in this currency — the only cash counted. */
        operating: z.string(),
        nonOperating: z.string(),
    }),
    restrictions: z.object({
        items: z.array(z.object({
            id: uuidSchema.nullable(),
            purpose: cashRestrictionPurposeSchema.nullable(),
            label: z.string(),
            amount: z.string(),
            effectiveTo: isoDateSchema.nullable(),
        })),
        total: z.string(),
    }),
    /** Approved distributions not yet paid — already promised to founders. */
    unpaidDistributions: z.object({
        items: z.array(z.object({
            proposalId: uuidSchema,
            number: z.number().int(),
            title: z.string().nullable(),
            outstanding: z.string(),
        })),
        total: z.string(),
    }),
    /** Restrictions + unpaid distributions (stored as `restricted_cash`). */
    committed: z.string(),
    reserve: z.object({
        policy: z
            .object({
            id: uuidSchema.nullable(),
            versionNumber: z.number().int().nullable(),
            policyType: reservePolicyTypeSchema,
            effectiveFrom: isoDateSchema.nullable(),
            summary: z.string(),
        })
            .nullable(),
        hybridRule: reserveHybridRuleSchema.nullable(),
        components: z.array(reserveComponentSchema),
        averageMonthlyExpenses: z
            .object({
            from: isoDateSchema,
            to: isoDateSchema,
            months: z.number().int(),
            monthsWithData: z.number().int(),
            total: z.string(),
            average: z.string(),
        })
            .nullable(),
        /** Null when it cannot be computed (no policy, missing FX rate). */
        required: z.string().nullable(),
    }),
    /** Accounting result of the period in this currency — shown for context; it does not decide what can be paid. */
    accounting: z.object({ revenue: z.string(), expenses: z.string(), result: z.string() }),
    /** operating − committed − reserve; null when the reserve cannot be computed. */
    distributable: z.string().nullable(),
    /** Largest pool that could be proposed: distributable rounded down to whole cents, never below 0. */
    maximumPool: z.string(),
    pool: z.string().nullable(),
    capTable: z
        .object({
        id: uuidSchema,
        versionNumber: z.number().int(),
        effectiveFrom: isoDateSchema,
        approvalDecisionId: uuidSchema.nullable(),
        approvalReference: z.string().nullable(),
    })
        .nullable(),
    allocations: z.array(distributionAllocationLineSchema),
    allocationExplanation: z.string().nullable(),
    obligations: z.array(founderObligationsSchema),
    /** Debts are never netted against distributions until a netting policy is approved (open decision 9). */
    netting: z.object({ applied: z.literal(false), note: z.string() }),
    warnings: z.array(distributionWarningSchema),
    /** True when any warning is BLOCKING. */
    blocked: z.boolean(),
});
// --- proposals ------------------------------------------------------------------------------------
export const distributionRefSchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string().nullable(),
});
export const distributionSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string().nullable(),
    status: distributionStatusSchema,
    pool: moneySchema,
    paid: moneySchema,
    outstanding: moneySchema,
    recordDate: isoDateSchema,
    period: z.object({ from: isoDateSchema, to: isoDateSchema }),
    decision: citedDecisionSchema.nullable(),
    createdAt: isoDateTimeSchema,
    approvedAt: isoDateTimeSchema.nullable(),
});
export const distributionPageSchema = cursorPageSchema(distributionSummarySchema);
export const distributionAllocationSchema = z.object({
    id: uuidSchema,
    founder: founderRefSchema,
    ownershipPercentage: z.string(),
    gross: moneySchema,
    netted: moneySchema,
    paid: moneySchema,
    outstanding: moneySchema,
    paymentStatus: distributionPaymentStatusSchema,
    calculationNote: z.string().nullable(),
});
export const distributionPaymentSchema = z.object({
    id: uuidSchema,
    allocationId: uuidSchema,
    founder: founderRefSchema,
    amount: moneySchema,
    paidOn: isoDateSchema,
    bankAccount: bankAccountRefSchema.nullable(),
    paymentReference: z.string().nullable(),
    status: documentStatusSchema,
    reversedAt: isoDateTimeSchema.nullable(),
    reversalReason: z.string().nullable(),
    recordedBy: z.string(),
    recordedAt: isoDateTimeSchema,
    journalEntryId: uuidSchema.nullable(),
    evidence: z.array(attachmentSchema),
});
/** Live re-check of a proposal against today's figures (proposals not yet approved). */
export const distributionCheckSchema = z.object({
    calculatedOn: isoDateSchema,
    distributable: z.string().nullable(),
    maximumPool: z.string(),
    /** Today's figures differ from the stored calculation — recalculate before approving. */
    stale: z.boolean(),
    differences: z.array(z.string()),
    blockers: z.array(distributionWarningSchema),
});
export const distributionDetailSchema = distributionSummarySchema.extend({
    calculation: distributionCalculationSchema.nullable(),
    check: distributionCheckSchema.nullable(),
    allocations: z.array(distributionAllocationSchema),
    payments: z.array(distributionPaymentSchema),
    journalEntries: z.array(journalEntryViewSchema),
    createdBy: z.string(),
    approvedBy: z.string().nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    cancelReason: z.string().nullable(),
    permissions: z.object({
        canEdit: z.boolean(),
        canApprove: z.boolean(),
        canCancel: z.boolean(),
        canRecordPayment: z.boolean(),
        canReversePayments: z.boolean(),
    }),
    version: z.number().int(),
});
export const distributionsOverviewSchema = z.object({
    asOf: isoDateSchema,
    /** Current position per currency with operating cash (no pool). */
    positions: z.array(distributionCalculationSchema),
    policy: reservePolicySchema.nullable(),
    pendingPolicies: z.number().int(),
    activeRestrictions: z.number().int(),
    proposals: z.array(distributionSummarySchema),
    permissions: z.object({
        canPropose: z.boolean(),
        canManage: z.boolean(),
    }),
});
// --- founder view ---------------------------------------------------------------------------------
export const founderDistributionSchema = z.object({
    allocationId: uuidSchema,
    distribution: distributionRefSchema.extend({
        status: distributionStatusSchema,
        recordDate: isoDateSchema,
    }),
    ownershipPercentage: z.string(),
    gross: moneySchema,
    netted: moneySchema,
    paid: moneySchema,
    outstanding: moneySchema,
    paymentStatus: distributionPaymentStatusSchema,
});
export const founderDistributionsSchema = z.object({
    items: z.array(founderDistributionSchema),
    totals: z.array(z.object({
        currency: z.string(),
        declared: z.string(),
        paid: z.string(),
        outstanding: z.string(),
    })),
});
// --- requests -------------------------------------------------------------------------------------
const proposalFields = {
    title: z.string().trim().min(1, 'Enter a title').max(200),
    currency: currencyCodeSchema,
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    recordDate: isoDateSchema,
    poolAmount: distributionAmountSchema,
};
function periodIsOrdered(value) {
    return value.periodEnd >= value.periodStart;
}
const PERIOD_ISSUE = { message: 'The period must end on or after its start', path: ['periodEnd'] };
export const createDistributionRequestSchema = z
    .object(proposalFields)
    .refine(periodIsOrdered, PERIOD_ISSUE);
export const updateDistributionRequestSchema = z
    .object({ ...proposalFields, version: versionSchema })
    .refine(periodIsOrdered, PERIOD_ISSUE);
export const approveDistributionRequestSchema = z.object({
    version: versionSchema,
    /** The finalized founder decision declaring the distribution (spec §7.16). */
    decisionId: uuidSchema,
});
export const cancelDistributionRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
export const recordDistributionPaymentRequestSchema = z.object({
    amount: distributionAmountSchema,
    paidOn: isoDateSchema,
    bankAccountId: uuidSchema,
    paymentReference: z.string().trim().max(200).nullable().default(null),
    evidence: z
        .array(createLinkAttachmentRequestSchema)
        .min(1, 'Add a link to the bank confirmation')
        .max(10),
});
export const reverseDistributionPaymentRequestSchema = z.object({ reason: reasonSchema });
/** What-if inputs; every override is optional and nothing is saved. */
export const distributionScenarioRequestSchema = z.object({
    currency: currencyCodeSchema,
    recordDate: isoDateSchema.nullable().default(null),
    poolAmount: distributionAmountSchema.nullable().default(null),
    operatingCash: signedAmountSchema.nullable().default(null),
    averageMonthlyExpenses: signedAmountSchema.nullable().default(null),
    additionalRestrictions: z
        .array(z.object({
        label: z.string().trim().min(1, 'Describe the amount').max(200),
        amount: distributionAmountSchema,
    }))
        .max(10)
        .default([]),
    /** Replaces the policy in force for this scenario only. */
    reservePolicy: z
        .object(reservePolicyFields)
        .nullable()
        .default(null)
        .superRefine((value, ctx) => {
        if (value === null)
            return;
        const parsed = proposeReservePolicyRequestSchema.safeParse(value);
        if (!parsed.success) {
            for (const issue of parsed.error.issues) {
                ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path });
            }
        }
    }),
});
export const distributionScenarioSchema = z.object({
    calculation: distributionCalculationSchema,
    /** Plain-language list of the assumptions that differ from the recorded figures. */
    assumptions: z.array(z.string()),
});
// --- queries --------------------------------------------------------------------------------------
export const distributionQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const cashRestrictionQuerySchema = z.object({
    status: cashRestrictionStatusSchema.optional(),
});
//# sourceMappingURL=distributions.js.map